import React, { useEffect } from "react";
import "./App.css";

import {
  Card,
  WixDesignSystemProvider,
  Button,
  MarketingLayout,
  Image,
  Page,
  Table,
  TableActionCell,
  Text,
  Modal,
  Box,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import { dashboard } from "@wix/dashboard";
import { createClient } from "@wix/sdk";
import * as Icons from "@wix/wix-ui-icons-common";
import { translationContents, translationSchemas } from "@wix/multilingual";

function inIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

function App() {
  const [client, setClient] = React.useState(null as any);
  const [totalWords, setTotalWords] = React.useState(0);
  const [schemas, setSchemas] = React.useState([] as any[]);
  const [selectedSchemaContent, setSelectedSchemaContent] = React.useState([] as any[]);
  const [selectedSchema, setSelectedSchema] = React.useState(null as any);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoadingContent, setIsLoadingContent] = React.useState(false);

  useEffect(() => {
    try {
      if (inIframe()) {
        var c = createClient({
          host: dashboard.host(),
          auth: dashboard.auth(),
          modules: {
            dashboard,
            translationContents,
            translationSchemas
          },
        });
        setClient(c);

                // Call translationContents API and log response
        c.translationContents.queryContents().find()
          .then((response) => {
            console.log('Translation contents response:', response);
            
            // Calculate total words from all fields
            let wordCount = 0;
            if (response.items && Array.isArray(response.items)) {
              response.items.forEach((item: any) => {
                if (item.fields) {
                  Object.values(item.fields).forEach((field: any) => {
                    if (field.numberOfWords) {
                      wordCount += field.numberOfWords;
                    }
                  });
                }
              });
            }
            setTotalWords(wordCount);
            setIsLoading(false);
          })
          .catch((error) => {
            console.error('Error fetching translation contents:', error);
            setIsLoading(false);
          });

        // Call translationSchemas API and log response
        c.translationSchemas.querySchemas().find()
          .then((response) => {
            console.log('Translation schemas response:', response);
            setSchemas(response.items || []);
          })
          .catch((error) => {
            console.error('Error fetching translation schemas:', error);
          });
      }
    } catch (error) {
      throw error;
    }
  }, []);

  const handleSchemaClick = async (schema: any) => {
    if (!client) return;
    
    setSelectedSchema(schema);
    setIsLoadingContent(true);
    
    try {
      const { items } = await client.translationContents.queryContents()
        .eq("schemaId", schema._id)
        .find();
      
      console.log(`Content for schema ${schema.displayName}:`, items);
      setSelectedSchemaContent(items || []);
    } catch (error) {
      console.error('Error fetching schema content:', error);
      setSelectedSchemaContent([]);
    } finally {
      setIsLoadingContent(false);
    }
  };

  // Add this handler inside App component
  const handleContentRowClick = async (row: any) => {
    if (!client || !row) return;
    const parentEntityId = row.entityId;
    const schemaIds = [selectedSchema?._id];
    try {
      const response = await client.translationContents.queryContents()
        .eq('parentEntityId', parentEntityId)
        .in('schemaId', schemaIds)
        .eq('locale', 'en')
        .find();
      console.log('Related contents response:', response);
    } catch (error) {
      console.error('Error fetching related contents:', error);
    }
  };

  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      <Page>
        <Page.Header title="Hire Translator" />
        <Page.Content>
          <Card>
            <MarketingLayout
              size="tiny"
              title={"Translation Content Analysis"}
              description={
                isLoading 
                  ? "Loading translation content..." 
                  : `Total word count across all translation fields: ${totalWords.toLocaleString()} words`
              }
              actions={
                !isLoading && (
                  <Button
                    size="small"
                    skin="ai"
                    suffixIcon={<Icons.AIFilledSmall />}
                    disabled
                  >
                    {`${totalWords.toLocaleString()} words`}
                  </Button>
                )
              }
            />
          </Card>
          
          {!isLoading && schemas.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <Card>
                <Card.Header title="Translation Schemas" />
                <Card.Content>
                  <Table
                    data={schemas}
                    onRowClick={handleSchemaClick}
                    columns={[
                      {
                        title: 'Display Name',
                        render: (row: any) => <Text>{row.displayName}</Text>,
                        width: '25%',
                      },
                      {
                        title: 'Entity Type',
                        render: (row: any) => <Text>{row.key?.entityType || 'N/A'}</Text>,
                        width: '30%',
                      },
                      {
                        title: 'Fields Count',
                        render: (row: any) => <Text>{Object.keys(row.fields || {}).length}</Text>,
                        width: '15%',
                      },
                      {
                        title: 'Revision',
                        render: (row: any) => <Text>{row.revision}</Text>,
                        width: '15%',
                      },
                      {
                        title: 'Hidden',
                        render: (row: any) => <Text>{row.hidden ? 'Yes' : 'No'}</Text>,
                        width: '15%',
                      },
                    ]}
                  />
                </Card.Content>
              </Card>
            </div>
          )}
        </Page.Content>
      </Page>
      
      <Modal
        isOpen={!!selectedSchema}
        onRequestClose={() => {
          setSelectedSchema(null);
          setSelectedSchemaContent([]);
        }}
        shouldCloseOnOverlayClick
        shouldDisplayCloseButton
      >
        <Box padding="20px">
          <Text weight="bold" size="medium" style={{ marginBottom: '16px' }}>
            Content for: {selectedSchema?.displayName || ''}
          </Text>
          {isLoadingContent ? (
          <Box textAlign="center" padding="20px">
            <Text>Loading content...</Text>
          </Box>
        ) : selectedSchemaContent.length > 0 ? (
          <Box>
            <Text weight="bold" size="medium" style={{ marginBottom: '16px' }}>
              Found {selectedSchemaContent.length} content items
            </Text>
            <Table
              data={selectedSchemaContent}
              onRowClick={handleContentRowClick}
              columns={[
                {
                  title: 'Entity ID',
                  render: (row: any) => <Text>{row.entityId}</Text>,
                  width: '20%',
                },
                {
                  title: 'Locale',
                  render: (row: any) => <Text>{row.locale}</Text>,
                  width: '10%',
                },
                {
                  title: 'Text Content',
                  render: (row: any) => {
                    const firstField = Object.values(row.fields || {})[0] as any;
                    const textValue = firstField?.textValue || '';
                    // Parse XML and extract readable text
                    const readableText = textValue
                      .replace(/<[^>]*>/g, ' ') // Remove XML tags
                      .replace(/&amp;/g, '&') // Decode HTML entities
                      .replace(/&lt;/g, '<')
                      .replace(/&gt;/g, '>')
                      .replace(/&quot;/g, '"')
                      .replace(/&#39;/g, "'")
                      .replace(/\s+/g, ' ') // Normalize whitespace
                      .trim();
                    return (
                      <Text size="small" style={{ maxWidth: '300px', wordBreak: 'break-word' }}>
                        {readableText || 'No text content'}
                      </Text>
                    );
                  },
                  width: '50%',
                },
                {
                  title: 'Publish Status',
                  render: (row: any) => <Text>{row.publishStatus}</Text>,
                  width: '20%',
                },
              ]}
            />
          </Box>
        ) : (
          <Box textAlign="center" padding="20px">
            <Text>No content found for this schema.</Text>
          </Box>
        )}
        </Box>
      </Modal>
    </WixDesignSystemProvider>
  );
}

export default App;